// Public types for the DataTable component system.
// Server-driven contract: response envelope is { data, total, page, limit }
// (matches packages/core/src/types/pagination.ts → PaginatedResponse<T>).

import type { ReactNode } from "react";

export type SortOrder = "asc" | "desc" | null;

export type FilterPrimitive = string | number | boolean;

export interface ListQuery {
  page: number;
  limit: number;
  sortBy?: string;
  order?: SortOrder;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  // Per-column filters land here keyed by accessorKey. Reserved keys above
  // are rejected by setFilter so query state stays well-formed.
  [key: string]: unknown;
}

export interface ColumnFilterOption {
  label: string;
  value: FilterPrimitive;
}

export type FilterType = "text" | "date" | "select";

export interface ColumnDef<T> {
  accessorKey: keyof T | string;
  header: string;
  cell?: (row: T) => ReactNode;
  sortable?: boolean;
  filterable?: boolean;
  filterType?: FilterType;
  filterOptions?: readonly ColumnFilterOption[];
  hidden?: boolean;
  width?: string;
  align?: "left" | "center" | "right";
}

export interface DataTableSelection {
  selected: ReadonlySet<string>;
  toggle: (id: string) => void;
  toggleAll: (ids: readonly string[]) => void;
  clear: () => void;
  isAllSelected: (ids: readonly string[]) => boolean;
  isSomeSelected: (ids: readonly string[]) => boolean;
}

export interface DataTableState {
  query: ListQuery;
  pendingSearch: string;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setSort: (sortBy: string) => void;
  setSearch: (value: string) => void;
  setDateRange: (from?: string, to?: string) => void;
  setFilter: (key: string, value: FilterPrimitive | undefined) => void;
  clearFilters: () => void;
  selection: DataTableSelection;
}

export interface DataTableProps<T> {
  state: DataTableState;
  columns: ReadonlyArray<ColumnDef<T>>;
  data: readonly T[];
  total: number;
  rowKey: (row: T) => string;
  ariaLabel: string;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  emptyState?: ReactNode;
  pageSizeOptions?: readonly number[];
  selectable?: boolean;
  bulkActions?: (selectedIds: readonly string[]) => ReactNode;
  searchPlaceholder?: string;
  enableDateRange?: boolean;
  className?: string;
  /** When set, the row whose rowKey() === highlightId gets the pulse animation
   *  + scrollIntoView. Used for "return from detail page" UX (TASK-52). */
  highlightId?: string;
}

export const DEFAULT_PAGE_SIZE_OPTIONS: readonly number[] = [20, 50, 100];
export const DEFAULT_SEARCH_DEBOUNCE_MS = 300;
